// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Log environment variables (without exposing full key)
console.log('Supabase URL:', import.meta.env.VITE_SUPABASE_URL);
console.log('Supabase Key exists:', !!import.meta.env.VITE_SUPABASE_ANON_KEY);
console.log('Supabase Key prefix:', import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 10) + '...');

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Environment variables check:', {
    hasUrl: !!supabaseUrl,
    hasKey: !!supabaseAnonKey,
    envKeys: Object.keys(import.meta.env)
  });
  throw new Error(
    'Missing Supabase environment variables. Please check your .env.local file and ensure both VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set.'
  );
}

// Validate Supabase URL format
if (!supabaseUrl.startsWith('https://') || !supabaseUrl.includes('.supabase.co')) {
  console.error('Invalid URL format:', supabaseUrl);
  throw new Error('Invalid Supabase URL format. URL should start with https:// and contain .supabase.co');
}

// Validate API key format (basic check)
if (!supabaseAnonKey.startsWith('eyJ')) {
  console.error('Invalid key format. Key prefix:', supabaseAnonKey.substring(0, 5));
  throw new Error('Invalid Supabase anon key format. The key should start with "eyJ"');
}

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
  db: {
    schema: 'public'
  }
});

// Test the connection immediately
(async () => {
  try {
    await supabase.from('tasks').select('count', { count: 'exact', head: true });
    console.log('Successfully connected to Supabase');
  } catch (error: any) {
    console.error('Error connecting to Supabase:', error.message);
    if (error.message.includes('Invalid API key')) {
      console.error('Please check your Supabase anon key in the .env.local file');
    }
  }
})();

// Temporary user ID for tasks until auth is implemented
export const TEMP_USER_ID = 'temp-user-1';

// Helper function to parse date strings
export const parseDate = (dateString: string | null): Date | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  return date;
};

// Helper function to parse JSON data with typed safety
export function parseTimeTracking(data: any): { totalSeconds: number; sessions: { start: Date; end?: Date }[] } | undefined {
  if (!data) return undefined;
  
  try {
    return {
      totalSeconds: typeof data.totalSeconds === 'number' ? data.totalSeconds : 0,
      sessions: Array.isArray(data.sessions) 
        ? data.sessions.map((session: any) => ({
            start: new Date(session.start),
            end: session.end ? new Date(session.end) : undefined
          }))
        : []
    };
  } catch (e) {
    console.error("Error parsing time tracking data:", e);
    return { totalSeconds: 0, sessions: [] };
  }
}

// Helper function to parse recurrence data
export function parseRecurrence(data: any): { 
  frequency: 'daily' | 'weekly' | 'monthly'; 
  interval: number; 
  endDate?: Date 
} | undefined {
  if (!data) return undefined;
  
  try {
    return {
      frequency: data.frequency as 'daily' | 'weekly' | 'monthly',
      interval: typeof data.interval === 'number' ? data.interval : 1,
      endDate: data.endDate ? new Date(data.endDate) : undefined
    };
  } catch (e) {
    console.error("Error parsing recurrence data:", e);
    return undefined;
  }
}

// Format task data for Supabase
export function formatTaskForSupabase(task: any) {
  const formattedTask = {
    title: task.title,
    category: task.category,
    priority: task.priority || 'medium',
    description: task.description,
    completed: task.completed || false,
    due_date: task.dueDate ? (task.dueDate instanceof Date ? task.dueDate.toISOString() : task.dueDate) : null,
    time_tracking: task.timeTracking ? {
      totalSeconds: task.timeTracking.totalSeconds,
      sessions: task.timeTracking.sessions.map((s: any) => ({
        start: s.start.toISOString(),
        end: s.end ? s.end.toISOString() : undefined
      }))
    } : null,
    is_recurring: task.isRecurring || false,
    recurrence: task.recurrence ? {
      frequency: task.recurrence.frequency,
      interval: task.recurrence.interval,
      endDate: task.recurrence.endDate?.toISOString()
    } : null,
    user_id: TEMP_USER_ID
  };

  // Remove any undefined values
  Object.keys(formattedTask).forEach(key => {
    if (formattedTask[key] === undefined) {
      delete formattedTask[key];
    }
  });

  return formattedTask;
}
